import InputField from '../../../components/Forms/Input/InputField';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import DatePicker from '../../../components/Forms/DatePicker/DatePicker';
import SelectField from '../../../components/Forms/Input/SelectField';
import ImportPurchaseFields from './ImportPurchaseFields';

import { useEffect } from 'react';
import { useBasePurchase } from '../../../hooks/purchases/useBasePurchase';
import { useDocumentType } from '../../../hooks/useDocumentType';
import { useSupplier } from '../../../hooks/useSupplier';
import { useAllowedOptions } from '../../../hooks/settings/useAllowedOptions';
import { useBranch } from '../../../hooks/branches/useBranch';
import { useAuthUser } from '../../../hooks/auth/useAuthUser';
import { validatePurchaseForm } from '../../../validations/purchases/purchaseValidation';
import QuickAddSupplier from '../../QuickAdd/Supplier/QuickAddSupplier';
import SectionInfoHeader from '../../common/SectionInfoHeader';
import SvgIcon from '../../SvgIcon/SvgIcon';
import { DOCUMENT_TYPE_CODES } from '../../../constants/documentTypeCodes';
import { getTodayDateString } from '../../../utils/dateFormatters';

const BasePurchaseForm = () => {
  const {
    form,
    updateField,
    formErrors,
    setErrors,
    handlerGenerate,
    clearDocumentNumber,
  } = useBasePurchase();

  const { fetchDocumentTypeDropdown, documentTypeDropdown } = useDocumentType();
  const { fetchBranchDropdown, branchDropdown, myBranch, fetchMyBranch } = useBranch();
  const { saveSupplier, handlerGetSuppliersForDropdown, suppliersDropdown } =
    useSupplier();
  const { getCodes, fetchCodesByModuleAndTarget } = useAllowedOptions();
  const { hasRole } = useAuthUser();

  const isImport = form.documentType?.code === '99';
  const isExcludedSubject =
    form.documentType?.code === DOCUMENT_TYPE_CODES.EXCLUDED_SUBJECT;

  // Solo estos roles pueden registrar la compra de Sujeto Excluido a nombre
  // de otra sucursal; para el resto se toma siempre la propia, sin mostrar
  // el selector.
  const canPickBranch = hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const allowedDocCodes = getCodes('PURCHASES', 'DOCUMENT_TYPE');

  // 📅 Fecha automática para Sujeto Excluido
  useEffect(() => {
    if (isExcludedSubject && !form.purchaseDate) {
      updateField('purchaseDate', getTodayDateString());
    }
  }, [isExcludedSubject]);

  const handleSave = async () => {
    const errors = validatePurchaseForm(form);

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    try {
      await handlerGenerate(form);
    } catch (err) {
      console.error('❌ Error al guardar la compra:', err);
    }
  };

  useEffect(() => {
    fetchDocumentTypeDropdown();
    fetchCodesByModuleAndTarget('PURCHASES', 'DOCUMENT_TYPE');
    handlerGetSuppliersForDropdown();
    fetchBranchDropdown(); // necesario para sujeto excluido
    fetchMyBranch();
  }, []);

  // Precarga la sucursal del usuario en Sujeto Excluido (editable si tiene el rol).
  useEffect(() => {
    if (isExcludedSubject && myBranch && !form.branchId) {
      updateField('branchId', myBranch.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExcludedSubject, myBranch]);

  return (
    <div className="mt-6 w-full rounded-sm border border-stroke bg-white p-6.5 shadow-default dark:border-strokedark dark:bg-boxdark">
      {/* ================= DOCUMENTO ================= */}
      <SectionInfoHeader
        icon={<SvgIcon name="document" />}
        title="Información del Documento"
        description="Completa los datos generales de la compra."
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        <SelectField
          label="Tipo de Documento"
          name="documentType"
          iconKey="list"
          value={form.documentType?.code || ''}
          onChange={(e) => {
            const code = e.target.value;
            const selected = documentTypeDropdown.find(d => d.code === code);
            if (!selected) return;

            clearDocumentNumber();

            updateField('documentType', {
              id: selected.id,
              name: selected.name,
              code: selected.code,
            });
          }}
          options={documentTypeDropdown
            .filter(doc => allowedDocCodes.includes(doc.code))
            .map(doc => ({
              value: doc.code,
              label: doc.name,
            }))}
          error={formErrors.documentType}
          required
        />

        {/* ❌ Número de documento SOLO si NO es Sujeto Excluido */}
        {!isExcludedSubject && (
          <InputField
            label="Número de Documento"
            name="documentNumber"
            iconKey="hash"
            type="text"
            value={form.documentNumber}
            onChange={(e) => updateField('documentNumber', e.target.value)}
            error={formErrors.documentNumber}
            required
          />
        )}
      </div>

      {/* ================= SUCURSAL (SOLO SUJETO EXCLUIDO) ================= */}
      {isExcludedSubject && canPickBranch && (
        <SelectField
          label="Sucursal"
          name="branchId"
          iconKey="store"
          value={form.branchId ?? ''}
          onChange={(e) => updateField('branchId', Number(e.target.value))}
          options={branchDropdown.map(b => ({
            value: b.id,
            label: b.name,
          }))}
          error={formErrors.branchId}
          required
        />
      )}

      {/* ================= PROVEEDOR ================= */}
      <SectionInfoHeader
        icon={<SvgIcon name="user" />}
        title="Proveedor"
        description="Selecciona el proveedor asociado a esta compra."
      />

      <QuickAddSupplier
        onReload={handlerGetSuppliersForDropdown}
        onSupplierCreated={saveSupplier}
      />

      <SimpleSearchSelect
        label="Proveedor"
        name="supplier"
        iconKey="listOrdered"
        options={suppliersDropdown}
        selectedValue={form.supplier?.id}
        onSelect={(option) => updateField('supplier', option)}
        error={formErrors.supplier}
        required
      />

      {/* ================= IMPORTACIÓN ================= */}
      {isImport && (
        <div className="mt-6">
          <ImportPurchaseFields
            form={form}
            formErrors={formErrors}
            updateField={updateField}
          />
        </div>
      )}

      {/* ================= FECHA ================= */}
      <div className="mt-6">
        <DatePicker
          label="Fecha de Compra"
          name="purchaseDate"
          iconKey="calendar"
          value={form.purchaseDate}
          onChange={(date) => updateField('purchaseDate', date)}
          error={formErrors.purchaseDate}
          required
          disabled={isExcludedSubject}
        />
      </div>

      {/* ================= BOTÓN ================= */}
      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          className="flex items-center justify-center gap-2 rounded bg-primary px-4.5 py-2.5 font-medium text-white hover:bg-opacity-90"
        >
          Generar Compra
        </button>
      </div>
    </div>
  );
};

export default BasePurchaseForm;
